// shadcn/ui button (CLI 4.21.0, new-york), rebound to docs/tokens.css.
// Changed from upstream: the control radius (8px — a rounded rectangle, not a
// capsule: UI-8 took the Mac a notch back), heights from the control tokens
// (34px, 30px above a table), a 180ms colour transition, focus left to the
// one global ring in app.css. Variants in the theme's words: the blue
// primary, the gray fill, the red tint for a stop that is a second choice.
import * as React from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-control border border-transparent text-body font-semibold transition-[color,background-color,box-shadow,opacity,filter] hover:no-underline active:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // The blue button: where something can be done.
        default: "bg-primary text-primary-foreground shadow-button hover:bg-primary/90",
        // Stopping, cancelling — the red.
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // The gray fill: every other action.
        outline: "bg-fill text-foreground hover:brightness-95 dark:hover:brightness-125",
        // A stop that is a second choice, not the obvious one: the red tint.
        "outline-danger": "bg-red-soft text-red-ink hover:brightness-95 dark:hover:brightness-125",
        secondary: "bg-blue-soft text-blue-ink hover:brightness-95 dark:hover:brightness-125",
        ghost: "font-medium text-muted-foreground hover:bg-fill hover:text-foreground",
        link: "h-auto! border-0 px-0! font-medium text-blue-ink underline-offset-4 hover:underline",
      },
      size: {
        default: "h-control px-3.5 has-[>svg]:px-3",
        sm: "h-control-sm px-3 text-detail has-[>svg]:px-2.5",
        icon: "size-control",
        "icon-sm": "size-control-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// forwardRef, not upstream's ref-as-prop: the admin is on React 18, where a
// function component drops `ref`, and Radix's `asChild` triggers (a menu or a
// tooltip anchored on a button) need the button's DOM node.
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(function Button({ className, variant = "default", size = "default", asChild = false, ...props }, ref) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
